import React, { useCallback, useMemo, useState } from 'react';
import { Image as ExpoImage } from 'expo-image';
import { StyleSheet, View } from 'react-native';

import { theme } from '@garnish/constants';

import { FadeView } from '../FadeView';
import { LoadingDots } from '../LoadingDots';
import { useImageSourceProps } from './hooks';
import { type ImageProps, type ImageWithLoadingProps } from './Image.types';
import { getImageForCurrentScreen, withHttpsProtocol } from './Image.utils';

// ─────────────────────────────────────────────────────────────────────────────

export const Image = (props: ImageProps) => {
  const {
    source,
    'aria-label': ariaLabel,
    staticSourceUrl,
    withLoadingSkeleton = false,
    ...rest
  } = props;

  // ─── Derived ─────────────────────────────────────────────────────────

  const formattedSource = useMemo(
    () => getImageForCurrentScreen(withHttpsProtocol(source)),
    [source],
  );

  const imageSourceProps = useImageSourceProps({
    source: formattedSource,
    staticSourceUrl,
  });

  // ─────────────────────────────────────────────────────────────────────

  if (withLoadingSkeleton) {
    return (
      <ImageWithLoading
        transition={theme.transitions.image}
        accessibilityLabel={ariaLabel}
        {...imageSourceProps}
        {...rest}
      />
    );
  }

  return (
    <ExpoImage
      loading="lazy"
      transition={theme.transitions.image}
      accessibilityLabel={ariaLabel}
      {...imageSourceProps}
      {...rest}
    />
  );
};

// ─── Variations ──────────────────────────────────────────────────────────────

const ImageWithLoading = (props: ImageWithLoadingProps) => {
  const { onLoadEnd, style, ...rest } = props;

  // ─── State ───────────────────────────────────────────────────────────

  const [isImageLoaded, setIsImageLoaded] = useState(false);

  // ─── Handlers ────────────────────────────────────────────────────────

  const handleImageLoad = useCallback(() => {
    setIsImageLoaded(true);
    onLoadEnd?.();
  }, [setIsImageLoaded, onLoadEnd]);

  // ─── Styles ──────────────────────────────────────────────────────────

  const imageStyles = [isImageLoaded ? undefined : styles.imageLoading, style];
  const loadingContainerStyles = [styles.loadingPlaceholderContainer, style];

  // ─────────────────────────────────────────────────────────────────────

  return (
    <View style={styles.loadingContainer}>
      <ExpoImage
        loading="lazy"
        transition={theme.transitions.image}
        onLoadEnd={handleImageLoad}
        style={imageStyles}
        {...rest}
      />

      <FadeView show={!isImageLoaded} style={loadingContainerStyles}>
        <LoadingDots color={theme.colors.CHARCOAL} />
      </FadeView>
    </View>
  );
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  loadingContainer: {
    width: '100%',
    height: '100%',
  },
  loadingPlaceholderContainer: {
    ...StyleSheet.absoluteFill,
    backgroundColor: theme.colors.LINEN,
  },
  imageLoading: {
    opacity: 0,
  },
});
